import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Trophy, Share2, Download } from "lucide-react";
import confetti from "canvas-confetti";
import { toast } from "sonner";

// Social media icons as simple SVG components
const FacebookIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
  </svg>
);

const LinkedInIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const XIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const CongratsModal = ({ open, onOpenChange, newTrophies, onClose, proudMoment, category }) => {
  const shareCardRef = useRef(null);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState(null);

  useEffect(() => {
    if (open) {
      // Fire confetti with emerald green colors
      const colors = ['#059669', '#10B981', '#D4AF37', '#3B82F6'];
      
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors
      });

      // Second burst
      setTimeout(() => {
        confetti({
          particleCount: 50,
          spread: 100,
          origin: { y: 0.5, x: 0.3 },
          colors
        });
        confetti({
          particleCount: 50,
          spread: 100,
          origin: { y: 0.5, x: 0.7 },
          colors
        });
      }, 200);
    }
  }, [open]);

  const generateShareImage = (platform) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set canvas size based on platform
      let width, height;
      switch (platform) {
        case 'facebook':
          width = 1200; height = 630; // Facebook optimal
          break;
        case 'instagram':
          width = 1080; height = 1080; // Instagram square
          break;
        case 'linkedin':
          width = 1200; height = 627; // LinkedIn optimal
          break;
        case 'x':
          width = 1200; height = 675; // Twitter/X optimal
          break;
        default:
          width = 1080; height = 1080;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Background gradient
      const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      gradient.addColorStop(0, '#ecfdf5');
      gradient.addColorStop(1, '#d1fae5');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Scale factor for different sizes
      const scale = width / 600;
      
      // App branding - top left
      ctx.font = `${14 * scale}px Arial`;
      ctx.fillStyle = '#6b7280';
      ctx.textAlign = 'left';
      ctx.fillText('Proud Every Day App', 20 * scale, 35 * scale);
      
      // Draw celebration emoji
      ctx.font = `${70 * scale}px Arial`;
      ctx.textAlign = 'center';
      const emojiY = platform === 'instagram' ? height * 0.2 : height * 0.22;
      ctx.fillText('🎉', width / 2, emojiY);
      
      // Draw "CONGRATULATIONS!"
      ctx.font = `italic bold ${42 * scale}px Georgia`;
      ctx.fillStyle = '#059669';
      const congratsY = platform === 'instagram' ? height * 0.32 : height * 0.38;
      ctx.fillText('CONGRATULATIONS!', width / 2, congratsY);
      
      // Draw "I'm proud that..."
      ctx.font = `${20 * scale}px Arial`;
      ctx.fillStyle = '#6b7280';
      const subtitleY = platform === 'instagram' ? height * 0.4 : height * 0.48;
      ctx.fillText("I'm proud that...", width / 2, subtitleY);
      
      // Draw the proud moment text (with word wrap)
      ctx.font = `bold ${24 * scale}px Arial`;
      ctx.fillStyle = '#1f2937';
      const maxWidth = width * 0.85;
      const lineHeight = 32 * scale;
      const words = (proudMoment || 'I did something amazing!').split(' ');
      let line = '';
      let y = platform === 'instagram' ? height * 0.48 : height * 0.58;
      const maxY = platform === 'instagram' ? height * 0.75 : height * 0.82;
      
      for (let n = 0; n < words.length; n++) {
        const testLine = line + words[n] + ' ';
        const metrics = ctx.measureText(testLine);
        if (metrics.width > maxWidth && n > 0) {
          ctx.fillText(line.trim(), width / 2, y);
          line = words[n] + ' ';
          y += lineHeight;
          if (y > maxY) break;
        } else {
          line = testLine;
        }
      }
      ctx.fillText(line.trim(), width / 2, y);
      
      // Draw category badge
      if (category) {
        ctx.font = `${16 * scale}px Arial`;
        ctx.fillStyle = '#059669';
        const catY = platform === 'instagram' ? height * 0.85 : height * 0.9;
        ctx.fillText(`#${category}  #ProudEveryDay`, width / 2, catY);
      }
      
      resolve(canvas);
    });
  };

  const copyImageToClipboard = async (canvas) => {
    try {
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob })
      ]);
      return true;
    } catch (err) {
      console.error('Failed to copy image:', err);
      return false;
    }
  };

  const downloadImage = async () => {
    const canvas = await generateShareImage('instagram');
    const link = document.createElement('a');
    link.download = 'proud-moment.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
    toast.success('Image downloaded!');
  };

  const shareToSocial = async (platform) => {
    // Generate the properly sized image for this platform
    const canvas = await generateShareImage(platform);
    
    // Try to copy to clipboard
    const copied = await copyImageToClipboard(canvas);
    
    const shareText = encodeURIComponent(`I'm proud that ${proudMoment || 'I did something amazing'}! #ProudEveryDay`);
    const shareUrl = encodeURIComponent(window.location.origin);
    
    let socialUrl = '';
    let toastMessage = copied 
      ? 'Image copied to clipboard! Paste it in your post.' 
      : 'Open the social network and share your achievement!';
    
    switch (platform) {
      case 'facebook':
        socialUrl = `https://www.facebook.com/`;
        toastMessage = copied ? 'Image copied! Create a new post and paste.' : 'Create a new post on Facebook!';
        break;
      case 'instagram':
        socialUrl = `https://www.instagram.com/`;
        toastMessage = copied ? 'Image copied! Create a new post and paste.' : 'Create a new post on Instagram!';
        break;
      case 'linkedin':
        socialUrl = `https://www.linkedin.com/feed/`;
        toastMessage = copied ? 'Image copied! Start a post and paste.' : 'Start a post on LinkedIn!';
        break;
      case 'x':
        socialUrl = `https://twitter.com/compose/tweet?text=${shareText}`;
        toastMessage = copied ? 'Image copied! Paste it in your tweet.' : 'Compose your tweet!';
        break;
      default:
        return;
    }
    
    // Show toast with instructions
    toast.success(toastMessage, { duration: 4000 });
    
    // Open the social network
    window.open(socialUrl, '_blank', 'width=800,height=600');
  };

  const handleShare = () => {
    setShowShareOptions(!showShareOptions);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md text-center" data-testid="congrats-modal">
        <DialogHeader className="sr-only">
          <DialogTitle>Congratulations</DialogTitle>
          <DialogDescription>You recorded a win</DialogDescription>
        </DialogHeader>
        
        {/* App branding - top left */}
        <div className="absolute top-4 left-4 text-xs text-muted-foreground font-medium">
          Proud Every Day App
        </div>
        
        <div className="py-6" ref={shareCardRef}>
          {/* Animated celebration icon */}
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="text-4xl animate-bounce">🎉</span>
          </div>

          <h2 className="text-3xl font-heading font-bold text-primary mb-2 italic" aria-hidden="true">
            CONGRATULATIONS!
          </h2>
          
          <p className="text-muted-foreground mb-4">
            You recorded a proud moment! Keep up the amazing work!
          </p>

          {/* Display the proud moment */}
          {proudMoment && (
            <div className="bg-emerald-50 dark:bg-emerald-950/30 rounded-xl p-4 mb-4 mx-2">
              <p className="text-sm text-muted-foreground mb-1">I'm proud that...</p>
              <p className="text-base font-medium text-foreground">
                "{proudMoment}"
              </p>
              {category && (
                <p className="text-xs text-emerald-600 mt-2">#{category}</p>
              )}
            </div>
          )}

          {/* New trophies earned */}
          {newTrophies && newTrophies.length > 0 && (
            <div className="bg-amber-400/20 rounded-xl p-4 mb-4">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Trophy className="w-5 h-5 text-amber-500" />
                <span className="font-semibold text-amber-700 dark:text-amber-300">
                  New Trophy{newTrophies.length > 1 ? 's' : ''} Earned!
                </span>
              </div>
              <div className="space-y-2">
                {newTrophies.map(trophy => (
                  <div 
                    key={trophy.id}
                    className="text-sm font-medium"
                  >
                    🏆 {trophy.name}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-col gap-3 items-center">
            <div className="flex gap-3 justify-center">
              <Button
                onClick={onClose}
                className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-full px-8"
                data-testid="congrats-close-btn"
              >
                Keep Going!
              </Button>
              <Button
                onClick={handleShare}
                variant="outline"
                className="rounded-full px-6"
                data-testid="share-btn"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
            </div>
            
            {/* Share options */}
            {showShareOptions && (
              <div className="flex flex-col items-center gap-2 pt-2 animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="flex gap-2 items-center justify-center">
                  <button
                    onClick={() => shareToSocial('facebook')}
                    className="p-2 rounded-full bg-[#1877F2] text-white hover:opacity-80 transition-opacity"
                    title="Share on Facebook"
                    data-testid="share-facebook"
                  >
                    <FacebookIcon />
                  </button>
                  <button
                    onClick={() => shareToSocial('instagram')}
                    className="p-2 rounded-full bg-gradient-to-tr from-[#FCAF45] via-[#E1306C] to-[#C13584] text-white hover:opacity-80 transition-opacity"
                    title="Share on Instagram"
                    data-testid="share-instagram"
                  >
                    <InstagramIcon />
                  </button>
                  <button
                    onClick={() => shareToSocial('linkedin')}
                    className="p-2 rounded-full bg-[#0A66C2] text-white hover:opacity-80 transition-opacity"
                    title="Share on LinkedIn"
                    data-testid="share-linkedin"
                  >
                    <LinkedInIcon />
                  </button>
                  <button
                    onClick={() => shareToSocial('x')}
                    className="p-2 rounded-full bg-black text-white hover:opacity-80 transition-opacity"
                    title="Share on X"
                    data-testid="share-x"
                  >
                    <XIcon />
                  </button>
                  <button
                    onClick={downloadImage}
                    className="p-2 rounded-full bg-gray-500 text-white hover:opacity-80 transition-opacity"
                    title="Download Image"
                    data-testid="download-image"
                  >
                    <Download className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  Click an icon to copy image & open network. Just paste!
                </p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CongratsModal;
